import { PageContext } from '@luminovo/api-contracts';
import { useRouteMatches, useRouteName } from '@luminovo/design-system';

/**
 * Captures what the user is currently looking at so the in-product agent can
 * resolve references like "this driver" / "this BOM" without the user re-typing
 * IDs. This page-awareness is the whole point of an in-product agent (a
 * bring-your-own agent can't see the screen).
 *
 * Not `useParams()`, which returns `{}` in the app-root drawer the Lumi panel is
 * mounted in (see useLumi); the router's matches hold anywhere under the Router.
 */
export function usePageContext(): PageContext | undefined {
    const leaf = useRouteMatches().at(-1);
    const pathname = leaf?.pathname ?? '';
    const routeName = useRouteName();
    // Decoded, so an id reads as the entity's own id.
    const leafParams = leaf?.params;
    if (!routeName) {
        // Unknown route — still hand the agent the path; it has no entity to resolve.
        return { route: pathname };
    }

    const ids: Record<string, string> = {};
    for (const [key, value] of Object.entries(leafParams ?? {})) {
        // A splat (`_splat`, and TanStack's `*` beside it) is no entity id, and may exceed the gateway's limit for one.
        if (key === '_splat' || key === '*') {
            continue;
        }
        if (value.length > 0) {
            ids[key] = value;
        }
    }

    return {
        route: pathname,
        pageType: routeName,
        pageTitle: sectionLabelFromRoute(routeName),
        entity: pickPrimaryEntity(routeName, ids),
        ids: Object.keys(ids).length > 0 ? ids : undefined,
    };
}

/**
 * The primary on-screen entity is the most specific route param — the last
 * `$param` in the route name that actually has a value. Its type is the param name
 * with the `Id` suffix stripped (driverId → driver, rfqId → rfq,
 * manufacturingAssemblyDetailsId → manufacturingAssemblyDetails).
 */
function pickPrimaryEntity(routeName: string, ids: Record<string, string>): PageContext['entity'] {
    // `$param`, and the `$param` inside an optional `{-$param}`.
    const paramNames = Array.from(routeName.matchAll(/\$([A-Za-z0-9_]+)/g), (m) => m[1]);
    for (let i = paramNames.length - 1; i >= 0; i--) {
        const param = paramNames[i];
        const id = ids[param];
        if (id) {
            return { type: param.replace(/Id$/, ''), id };
        }
    }
    return undefined;
}

// Human labels for the product's sections (the segment users recognize), so the
// page title is "BOM" / "Manufacturing" / "Sourcing" rather than a route artifact
// like "Details" or "Assembly". Unmapped segments fall back to Title Case, so new
// routes still get a reasonable label without a code change here.
const SECTION_LABELS: Record<string, string> = {
    bom: 'BOM',
    manufacturing: 'Manufacturing',
    sourcing: 'Sourcing',
    calculation: 'Calculation',
    'flexible-calculation': 'Calculation',
    quotation: 'Quotation',
    quote: 'Quotation',
    order: 'Order',
    orders: 'Orders',
    'purchase-orders': 'Purchase orders',
    dashboard: 'Dashboard',
    parts: 'Parts',
    assemblies: 'Assembly',
    suppliers: 'Suppliers',
    supplier: 'Supplier',
    'supplier-portal': 'Supplier portal',
    customers: 'Customers',
    negotiations: 'Negotiation',
    offers: 'Offers',
    templates: 'Templates',
    'risk-management': 'Risk management',
    monitoring: 'Monitoring',
    settings: 'Settings',
    pcb: 'PCB',
};

/**
 * Human-readable title for the page, derived from its SECTION segment — the
 * module the user recognizes (Manufacturing, BOM, Sourcing, …). For project
 * routes that's the first static segment after the project id (and a bare project
 * overview is "Project"); otherwise the first static segment. This avoids
 * trailing route artifacts ("Details", "Edit") that the last-segment approach hit.
 */
function sectionLabelFromRoute(routeName: string): string | undefined {
    const segments = routeName.split('/').filter((segment) => segment.length > 0);
    // `$param`, `{-$param}`, and a splat's `$`.
    const isParam = (segment: string) => segment.startsWith('$') || segment.startsWith('{-$');

    let section: string | undefined;
    if (segments[0] === 'projects') {
        section = segments.slice(2).find((segment) => !isParam(segment));
        if (!section) {
            return 'Project';
        }
    } else {
        section = segments.find((segment) => !isParam(segment));
    }
    if (!section) {
        return undefined;
    }

    return SECTION_LABELS[section] ?? section.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
